import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@c2fo/liquidity';
import type { ReportIssueType } from '../InsightsPage.types';

const REPORT_ISSUE_TYPES: readonly ReportIssueType[] = [
  'incorrect-response',
  'incomplete-response',
  'incorrect-sql',
  'performance-issue',
  'other',
];

const isReportIssueType = (value: string): value is ReportIssueType =>
  REPORT_ISSUE_TYPES.some((reportIssueType) => reportIssueType === value);

interface InsightsReportIssueModalProps {
  reportIssueMessageId: string | null;
  reportIssueType: ReportIssueType;
  reportIssueDescription: string;
  isSubmittingReport: boolean;
  reportSubmitStatus: 'idle' | 'success' | 'error';
  onReportIssueTypeChange: (nextType: ReportIssueType) => void;
  onReportIssueDescriptionChange: (nextDescription: string) => void;
  onClose: () => void;
  onSubmit: () => void;
}

export const InsightsReportIssueModal = ({
  reportIssueMessageId,
  reportIssueType,
  reportIssueDescription,
  isSubmittingReport,
  reportSubmitStatus,
  onReportIssueTypeChange,
  onReportIssueDescriptionChange,
  onClose,
  onSubmit,
}: InsightsReportIssueModalProps) => {
  if (!reportIssueMessageId) {
    return null;
  }

  return (
    <div className="report-issue-modal-overlay" role="presentation">
      <div className="report-issue-modal" role="dialog" aria-modal="true" aria-labelledby="report-issue-title">
        <div className="report-issue-modal-header">
          <h2 id="report-issue-title">Report Issue</h2>
          <button type="button" className="report-issue-modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="report-issue-modal-body">
          <div className="report-issue-field">
            <label htmlFor="issue-type" className="report-issue-label">
              Issue Type
            </label>
            <Select
              value={reportIssueType}
              onValueChange={(value: string) => {
                if (!isReportIssueType(value)) {
                  return;
                }

                onReportIssueTypeChange(value);
              }}
              disabled={isSubmittingReport}
            >
              <SelectTrigger id="issue-type" className="report-issue-select" aria-label="Issue type">
                <SelectValue placeholder="Select issue type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="incorrect-response">Incorrect Response</SelectItem>
                <SelectItem value="incomplete-response">Incomplete Response</SelectItem>
                <SelectItem value="incorrect-sql">Incorrect SQL</SelectItem>
                <SelectItem value="performance-issue">Performance Issue</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="report-issue-field">
            <label htmlFor="issue-description" className="report-issue-label">
              Description
            </label>
            <textarea
              id="issue-description"
              className="report-issue-textarea"
              value={reportIssueDescription}
              onChange={(event) => onReportIssueDescriptionChange(event.target.value)}
              placeholder="Describe the issue you encountered..."
              rows={6}
              disabled={isSubmittingReport}
            />
          </div>

          {reportSubmitStatus === 'success' && (
            <div className="report-issue-status report-issue-status-success">✓ Report submitted successfully</div>
          )}

          {reportSubmitStatus === 'error' && (
            <div className="report-issue-status report-issue-status-error">Failed to submit. Please try again.</div>
          )}
        </div>

        <div className="report-issue-modal-footer">
          <Button type="button" variant="secondary" size="md" onClick={onClose} disabled={isSubmittingReport}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="cta"
            size="md"
            onClick={onSubmit}
            disabled={!reportIssueDescription.trim() || isSubmittingReport}
          >
            {isSubmittingReport ? 'Submitting...' : 'Submit Report'}
          </Button>
        </div>
      </div>
    </div>
  );
};
